/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useState, type ChangeEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type AuditFilters, type ClinicListItem } from '../../lib/api/sa.js';
import { useSaAudit, useSaClinics } from '../../lib/queries/sa.js';

import { AuditTableRow } from './audit-row.js';

const INPUT_CLASS =
  'focus-ring h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const HEAD_CLASS =
  'border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400';
const AUDIT_LIMIT = 100;

interface FilterBarProps {
  readonly filters: AuditFilters;
  readonly clinics: readonly ClinicListItem[];
  readonly onChange: (next: AuditFilters) => void;
}

function FilterBar({ filters, clinics, onChange }: FilterBarProps): JSX.Element {
  const { t } = useTranslation();
  const onClinic = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...filters, clinicId: e.target.value });
    },
    [filters, onChange],
  );
  const onAction = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange({ ...filters, action: e.target.value });
    },
    [filters, onChange],
  );
  const onFrom = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange({ ...filters, from: e.target.value });
    },
    [filters, onChange],
  );
  const onTo = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange({ ...filters, to: e.target.value });
    },
    [filters, onChange],
  );
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <select
        value={filters.clinicId ?? ''}
        onChange={onClinic}
        aria-label={t('sa.audit.filterClinic')}
        className={INPUT_CLASS}
      >
        <option value="">{t('sa.audit.allClinics')}</option>
        {clinics.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <input
        type="search"
        value={filters.action ?? ''}
        onChange={onAction}
        placeholder={t('sa.audit.filterAction')}
        aria-label={t('sa.audit.filterAction')}
        className={INPUT_CLASS}
      />
      <input
        type="date"
        value={filters.from ?? ''}
        onChange={onFrom}
        aria-label={t('sa.audit.filterFrom')}
        className={INPUT_CLASS}
      />
      <input
        type="date"
        value={filters.to ?? ''}
        onChange={onTo}
        aria-label={t('sa.audit.filterTo')}
        className={INPUT_CLASS}
      />
    </div>
  );
}

/** Super Admin → Audit (`/sa/audit`). Filterable platform audit log. */
export function SaAuditPage(): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditFilters>({ limit: AUDIT_LIMIT });
  const clinics = useSaClinics();
  const audit = useSaAudit(filters);
  const rows = audit.data ?? [];

  return (
    <div className="p-8">
      <PageHeader title={t('sa.audit.title')} subtitle={t('sa.audit.subtitle')} />
      <FilterBar filters={filters} clinics={clinics.data ?? []} onChange={setFilters} />

      <QueryState
        isLoading={audit.isLoading}
        isError={audit.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('sa.audit.empty')}
      >
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full text-left text-sm">
            <thead className={HEAD_CLASS}>
              <tr>
                <th className="px-5 py-3 font-semibold">{t('sa.audit.colTime')}</th>
                <th className="px-5 py-3 font-semibold">{t('sa.audit.colClinic')}</th>
                <th className="px-5 py-3 font-semibold">{t('sa.audit.colActor')}</th>
                <th className="px-5 py-3 font-semibold">{t('sa.audit.colAction')}</th>
                <th className="px-5 py-3 font-semibold">{t('sa.audit.colEntity')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((entry) => (
                <AuditTableRow
                  key={`${entry.at}-${entry.action}-${entry.entityId ?? ''}`}
                  entry={entry}
                />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </div>
  );
}
